// src/components/Admin/ProtectedRoute.js
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading, true/false = determined
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let sessionTimeoutCleanup;
    
    const checkAuth = async () => {
      const user = authService.getCurrentUser();
      
      if (user) {
        const adminStatus = await authService.isAdmin(user);
        setIsAuthenticated(true);
        setIsAdmin(adminStatus);
        
        // Setup session timeout
        sessionTimeoutCleanup = authService.setupSessionTimeout(30); // 30 minutes
        
        // Listen for session timeout events
        const handleSessionTimeout = () => {
          alert('Your session has expired. Please log in again.');
          setIsAuthenticated(false);
          setIsAdmin(false);
        };
        
        window.addEventListener('sessionTimeout', handleSessionTimeout);
        
        return () => {
          window.removeEventListener('sessionTimeout', handleSessionTimeout);
        };
      } else {
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    };

    // Set up auth state listener
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      if (user) {
        const adminStatus = await authService.isAdmin(user);
        setIsAuthenticated(true);
        setIsAdmin(adminStatus);
        
        // Setup session timeout if not already set up
        if (!sessionTimeoutCleanup) {
          sessionTimeoutCleanup = authService.setupSessionTimeout(30);
        }
      } else {
        setIsAuthenticated(false);
        setIsAdmin(false);
        
        // Clean up session timeout
        if (sessionTimeoutCleanup) {
          sessionTimeoutCleanup();
          sessionTimeoutCleanup = null;
        }
      }
    });

    // Initial check
    checkAuth();

    // Cleanup function
    return () => {
      unsubscribe();
      if (sessionTimeoutCleanup) {
        sessionTimeoutCleanup();
      }
    };
  }, []);

  // Show loading spinner while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3">
            <p className="text-muted">Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // If authenticated but not admin, show access denied
  if (isAuthenticated && !isAdmin) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="mb-4">
            <i className="bi bi-shield-exclamation text-danger" style={{ fontSize: '4rem' }}></i>
          </div>
          <h2 className="text-danger mb-3">Access Denied</h2>
          <p className="lead text-muted mb-4">
            You don't have permission to access this area.
          </p>
          <p className="text-muted mb-4">
            Admin privileges are required to access the admin panel.
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <button 
              className="btn btn-outline-secondary"
              onClick={() => window.history.back()}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Go Back
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => authService.signOut().then(() => window.location.reload())}
            >
              <i className="bi bi-box-arrow-right me-2"></i>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated and admin, render the protected component
  return children;
};

// Higher-order component for easier usage
export const withProtectedRoute = (Component) => {
  return (props) => (
    <ProtectedRoute>
      <Component {...props} />
    </ProtectedRoute>
  );
};

export default ProtectedRoute;